/* 
 * src/features/scope_module/utils/strategySvgs.js
 * Overlays (image-pixel space, no axes):
 *  - Coverage Opportunity Grid (kept)
 *  - Optimized Route (ROI sweep)
 *  - Confidence Halos (evidence support)
 *  - Hesitation Map (uncertainty hotspots)
 *  - Landscape poster (10×8)
 */

function cssVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function fmt(n, d = 1) { return Number.isFinite(n) ? n.toFixed(d) : String(n); }
function hexToRgb(hex) {
  const h = hex.replace('#','');
  const bigint = parseInt(h.length===3 ? h.split('').map(ch => ch+ch).join('') : h, 16);
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}
function luminance(r,g,b) { return (0.2126*r + 0.7152*g + 0.0722*b) / 255; }
function dist(a,b){ const dx=a.x-b.x, dy=a.y-b.y; return Math.hypot(dx,dy); }
function angle(a,b,c){ // ∠abc
  const ux=a.x-b.x, uy=a.y-b.y, vx=c.x-b.x, vy=c.y-b.y;
  const d = (ux*vx + uy*vy) / (Math.hypot(ux,uy)*Math.hypot(vx,vy) + 1e-9);
  return Math.acos(Math.max(-1, Math.min(1, d)));
}

// XML escape for text/attrs
function xmlEscape(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Encode an SVG string as a data URI for safe embedding/rasterization
export function svgToDataUri(svgString) {
  const encoded = btoa(unescape(encodeURIComponent(svgString)));
  return `data:image/svg+xml;base64,${encoded}`;
}

/** Raster an image into a cols×rows grid (returns normalized [0..1]) */
export function computeGridFromImage(img, W, H, cols, rows) {
  if (!img || !cols || !rows || !W || !H) return null;
  try {
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(img, 0, 0, W, H);

    const cellW = Math.max(1, Math.floor(W / cols));
    const cellH = Math.max(1, Math.floor(H / rows));
    const out = new Array(rows * cols).fill(0);

    for (let ry = 0; ry < rows; ry++) {
      for (let cx = 0; cx < cols; cx++) {
        const x = cx * cellW;
        const y = ry * cellH;
        const sw = (cx === cols - 1) ? (W - x) : cellW;
        const sh = (ry === rows - 1) ? (H - y) : cellH;
        const data = g.getImageData(x, y, sw, sh).data;
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) sum += luminance(data[i], data[i+1], data[i+2]);
        out[ry * cols + cx] = sum / (data.length / 4);
      }
    }
    const mx = Math.max(...out), mn = Math.min(...out);
    const span = Math.max(1e-6, mx - mn);
    return out.map(v => (v - mn) / span);
  } catch {
    return null;
  }
}

/** Snapshot an image as dataURL for background embedding */
export function imageToDataUrl(img, W, H) {
  try {
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d');
    g.drawImage(img, 0, 0, W, H);
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

/* -------------------------------------------------------
 * COVERAGE OPPORTUNITY GRID (KEPT)
 * -----------------------------------------------------*/
export function generateCoverageGridSVG({
  imageWidth: W,
  imageHeight: H,
  path,                // [{x,y,t}] trimmed
  grid = { cols: 10, rows: 8 },
  clarityGrid = null,  // optional array length rows*cols in [0,1]
  bgImageDataUrl = null,
  showLegend = true,
  topN = 5,
  alphaDwell = 1.0
}) {
  const cols = grid.cols || 10, rows = grid.rows || 8;
  const cellW = W / cols, cellH = H / rows;

  const accent = cssVar('--accent-color', '#1982c4');
  const warn   = cssVar('--warning-color', '#f1c40f');
  const textP  = cssVar('--text-primary', '#e0e0e0');
  const textS  = cssVar('--text-secondary', '#b0b0b0');
  const border = cssVar('--border', '#404040');

  // Dwell & visits
  const dwell = Array.from({ length: rows * cols }, () => 0);
  const visited = Array.from({ length: rows * cols }, () => false);

  if (Array.isArray(path) && path.length > 1) {
    let prev = path[0];
    for (let i = 1; i < path.length; i++) {
      const p = path[i];
      const dt = Math.max(0, (p.t ?? 0) - (prev.t ?? 0));
      const cx = clamp(Math.floor(prev.x / cellW), 0, cols - 1);
      const cy = clamp(Math.floor(prev.y / cellH), 0, rows - 1);
      const idx = cy * cols + cx;
      dwell[idx] += dt;
      visited[idx] = true;
      prev = p;
    }
  }

  const totalDwell = dwell.reduce((s, c) => s + c, 0);
  const maxDwell = dwell.reduce((m, c) => Math.max(m, c), 0) || 1;
  const coverageFrac = dwell.map(d => d / Math.max(1, totalDwell));
  // Fallback clarity = 1 so gap reduces to (1 - coverage) if no clarity
  const clarity = clarityGrid || new Array(rows * cols).fill(1);

  // Gap score: high clarity but low coverage
  const gap = clarity.map((q, i) => q * (1 - coverageFrac[i]));
  const ranked = Array.from({ length: rows * cols }, (_, i) => i).sort((a, b) => gap[b] - gap[a]);
  const suggestions = ranked.slice(0, topN);

  let svg = '';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;
  svg += `<title>${xmlEscape('Coverage Opportunity Grid')}</title>`;
  svg += `<desc>${xmlEscape('Dwell shaded; clarity-driven revisit suggestions labeled.')}</desc>`;

  if (bgImageDataUrl) {
    svg += `<image href="${xmlEscape(bgImageDataUrl)}" x="0" y="0" width="${W}" height="${H}" opacity="0.25" />`;
  } else {
    svg += `<rect x="0" y="0" width="${W}" height="${H}" fill="none" stroke="${border}" stroke-width="1"/>`;
  }

  // Cells: dwell alpha, clarity dot, dashed border if unvisited
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const x = c * cellW, y = r * cellH;
      const a = alphaDwell * Math.sqrt(dwell[idx] / maxDwell);
      const fill = `rgba(${hexToRgb(accent)}, ${clamp(a,0,1).toFixed(3)})`;
      const stroke = visited[idx] ? border : warn;
      const dash = visited[idx] ? '' : ' stroke-dasharray="6,4"';
      svg += `<rect x="${x}" y="${y}" width="${cellW}" height="${cellH}" fill="${fill}" stroke="${stroke}" stroke-width="1"${dash}/>`;

      // clarity dot
      const cx = x + cellW / 2, cy = y + cellH / 2;
      const dotR = Math.max(2, Math.min(8, 2 + (clarity[idx] ?? 0) * 6));
      svg += `<circle cx="${fmt(cx,1)}" cy="${fmt(cy,1)}" r="${fmt(dotR,1)}" fill="${warn}" opacity="0.75">
        <title>${xmlEscape(`Cell (c${c+1}, r${r+1}) • Coverage ${fmt(coverageFrac[idx]*100,1)}% • Clarity ${fmt(clarity[idx]??0,2)} • Gap ${fmt(gap[idx],2)}`)}</title>
      </circle>`;
    }
  }

  // Top-N labels
  suggestions.forEach((idx, rank) => {
    const r = Math.floor(idx / cols), c = idx % cols;
    const x = c * cellW, y = r * cellH;
    svg += `<rect x="${fmt(x+2,1)}" y="${fmt(y+2,1)}" width="22" height="18" fill="${cssVar('--warning-color','#f1c40f')}" opacity="0.95" rx="3" ry="3"/>`;
    svg += `<text x="${fmt(x+13,1)}" y="${fmt(y+15,1)}" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12" fill="#000">${xmlEscape(`#${rank+1}`)}</text>`;
  });

  if (showLegend) {
    const pad = 10;
    const pctVisited = 100 * (visited.filter(Boolean).length / visited.length);
    const dwellSec = totalDwell / 1000;
    svg += `<g font-family="system-ui, sans-serif" font-size="12" fill="${textP}">
      <text x="${pad}" y="${pad+14}">${xmlEscape('Coverage: dwell (blue), clarity dot (yellow), # = revisit rank')}</text>
      <text x="${pad}" y="${pad+32}" fill="${textS}">${xmlEscape(`Visited: ${fmt(pctVisited,1)}% · Total dwell: ${fmt(dwellSec,1)}s`)}</text>
    </g>`;
  }

  svg += `</svg>`;
  return svg;
}

/* -------------------------------------------------------
 * DYNAMIC STRATEGY VISUALIZATION (replaces optimized route)
 * Based on dynamic equilibrium metrics from AI Build Brief
 * -----------------------------------------------------*/

// Helper functions for strategy calculations
function computeEvidenceStrength(clarityGrid, dwell, cols, rows) {
  // Quality score (clarity)
  const clarityMean = clarityGrid ?
    clarityGrid.reduce((s, v) => s + v, 0) / clarityGrid.length : 0.5;
  const clarityVar = clarityGrid ?
    clarityGrid.reduce((s, v) => s + Math.pow(v - clarityMean, 2), 0) / clarityGrid.length : 0;
  const clarityVarNorm = Math.min(1, clarityVar / 0.25);
  const qualityScore = clamp(clarityMean * (1 - 0.3 * clarityVarNorm), 0, 1);

  // Quantity score (based on dwell distribution)
  const totalDwell = dwell.reduce((s, d) => s + d, 0) || 1;
  const activeCells = dwell.filter(d => d > 0).length;
  const expectedCells = cols * rows * 0.6; // expect 60% coverage
  const quantityScore = clamp(activeCells / expectedCells, 0, 1);

  // Coverage score (uniformity of dwell)
  const dwellNorm = dwell.map(d => d / totalDwell);
  const expectedUniform = 1 / (cols * rows);
  const uniformityScore = 1 - dwellNorm.reduce((s, d) => s + Math.abs(d - expectedUniform), 0);
  const coverageScore = clamp(uniformityScore * 2, 0, 1);

  // Evidence strength (geometric mean)
  const evidenceStrength = Math.pow(qualityScore * quantityScore * coverageScore, 1/3);
  const complexity = 1 - evidenceStrength;

  return { qualityScore, quantityScore, coverageScore, evidenceStrength, complexity };
}

function computeStrategyTargets(complexity) {
  const lerp = (a, b, t) => a + (b - a) * t;
  return {
    explore: lerp(0.2, 0.8, complexity),
    accommodate: lerp(0.2, 0.8, complexity),
    resilience: lerp(0.2, 0.8, complexity)
  };
}

export function generateOptimizedRouteSVG({
  imageWidth: W, imageHeight: H,
  path, // trimmed [{x,y,t}]
  grid = { cols: 10, rows: 8 },
  clarityGrid = null,
  bgImageDataUrl = null,
  features = [], // minutiae features
  currentStrategy = { explore: 0.5, accommodate: 0.5, resilience: 0.5 }
}) {
  const cols = grid.cols, rows = grid.rows;
  const cellW = W / cols, cellH = H / rows;

  const border = cssVar('--border', '#404040');
  const textP = cssVar('--text-primary', '#e0e0e0');
  const textS = cssVar('--text-secondary', '#b0b0b0');
  const accent = cssVar('--accent-color', '#1982c4');
  const warn = cssVar('--warning-color', '#f1c40f');

  // Calculate dwell per cell
  const dwell = new Array(rows * cols).fill(0);
  if (Array.isArray(path) && path.length > 1) {
    let prev = path[0];
    for (let i = 1; i < path.length; i++) {
      const p = path[i];
      const dt = Math.max(0, (p.t ?? 0) - (prev.t ?? 0));
      const cx = clamp(Math.floor(prev.x / cellW), 0, cols - 1);
      const cy = clamp(Math.floor(prev.y / cellH), 0, rows - 1);
      dwell[cy * cols + cx] += dt;
      prev = p;
    }
  }

  // Compute evidence and strategy metrics
  const evidence = computeEvidenceStrength(clarityGrid, dwell, cols, rows);
  const targets = computeStrategyTargets(evidence.complexity);

  // Determine complexity band and posture
  const band = evidence.complexity >= 0.66 ? "High" :
               evidence.complexity >= 0.33 ? "Medium" : "Low";
  const bandColor = band === "High" ? "#c0392b" :
                   band === "Medium" ? "#f39c12" : "#2ecc71";

  // SVG
  let svg = '';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;
  svg += `<title>${xmlEscape('Dynamic Strategy Visualization')}</title>`;
  svg += `<desc>${xmlEscape('Evidence strength and strategic recommendations based on dynamic equilibrium')}</desc>`;

  if (bgImageDataUrl) {
    svg += `<image href="${xmlEscape(bgImageDataUrl)}" x="0" y="0" width="${W}" height="${H}" opacity="0.3" />`;
  } else {
    svg += `<rect x="0" y="0" width="${W}" height="${H}" fill="none" stroke="${border}" stroke-width="1"/>`;
  }

  // Add strategy-relevant overlay on fingerprint image
  // Show areas by their strategic value - complexity drives explore vs exploit
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (dwell[idx] <= 0) continue;

      const x = c * cellW, y = r * cellH;
      const dwellIntensity = dwell[idx] / Math.max(...dwell);
      const clarity = clarityGrid ? clarityGrid[idx] || 0 : 0.5;

      // Strategic value: high complexity areas need exploration, low complexity can be exploited
      const localComplexity = 1 - (clarity * dwellIntensity);
      const strategicNeed = localComplexity > 0.6 ? "explore" : localComplexity > 0.3 ? "balance" : "exploit";

      // Color coding: blue=explore needed, purple=balance, orange=exploit ready
      const color = strategicNeed === "explore" ? "#3498db" :
                   strategicNeed === "balance" ? "#9b59b6" : "#e67e22";

      svg += `<rect x="${fmt(x,1)}" y="${fmt(y,1)}" width="${fmt(cellW,1)}" height="${fmt(cellH,1)}"
              fill="${color}" opacity="${fmt(0.15 + 0.25 * dwellIntensity, 2)}"/>`;
    }
  }

  // Mark minutiae with strategy relevance
  if (Array.isArray(features) && features.length > 0) {
    features.forEach(feature => {
      if (feature && feature.x !== undefined && feature.y !== undefined) {
        const isAdd = feature.op === 'add' || !feature.op;
        // Features in low-clarity areas suggest need for exploration
        const featureClarity = clarityGrid ?
          clarityGrid[Math.floor(feature.y / cellH) * cols + Math.floor(feature.x / cellW)] || 0.5 : 0.5;
        const needsExploration = featureClarity < 0.4;

        const color = isAdd ? (needsExploration ? "#3498db" : "#27ae60") : "#e74c3c";
        svg += `<circle cx="${feature.x}" cy="${feature.y}" r="3" fill="${color}" opacity="0.8" stroke="#fff" stroke-width="1"/>`;
      }
    });
  }

  // Strategy scale (left side)
  const strategyApproaches = [
    { name: "Explore", value: targets.explore, current: currentStrategy.explore, color: "#3498db", description: "Seek new patterns & features" },
    { name: "Accommodate", value: targets.accommodate, current: currentStrategy.accommodate, color: "#9b59b6", description: "Adapt to new evidence" },
    { name: "Resilience", value: targets.resilience, current: currentStrategy.resilience, color: "#e67e22", description: "Maintain stable interpretations" }
  ];

  const scaleX = 15, scaleY = 40, scaleItemH = 45;
  svg += `<rect x="${scaleX - 5}" y="${scaleY - 10}" width="110" height="${strategyApproaches.length * scaleItemH + 30}"
          fill="#000" opacity="0.3" rx="8"/>`;
  svg += `<text x="${scaleX + 50}" y="${scaleY - 2}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="${textP}">
          Strategy Dimensions</text>`;

  strategyApproaches.forEach((strategy, i) => {
    const itemY = scaleY + 15 + i * scaleItemH;
    const gap = Math.abs(strategy.current - strategy.value);
    const alignment = 1 - gap;
    const alignmentColor = alignment >= 0.8 ? "#27ae60" : alignment >= 0.6 ? "#f39c12" : "#e74c3c";

    // Strategy indicator circle
    svg += `<circle cx="${scaleX + 15}" cy="${itemY + 10}" r="8" fill="${strategy.color}" opacity="0.8"/>`;
    svg += `<text x="${scaleX + 15}" y="${itemY + 14}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="8" font-weight="bold" fill="#fff">
            ${strategy.name.charAt(0)}</text>`;

    // Strategy name and description
    svg += `<text x="${scaleX + 28}" y="${itemY + 8}" font-family="system-ui,sans-serif" font-size="9" font-weight="bold" fill="${textP}">
            ${xmlEscape(strategy.name)}</text>`;
    svg += `<text x="${scaleX + 28}" y="${itemY + 18}" font-family="system-ui,sans-serif" font-size="7" fill="${textS}">
            ${xmlEscape(strategy.description)}</text>`;

    // Current vs target bars
    const barY = itemY + 22;
    svg += `<rect x="${scaleX + 28}" y="${barY}" width="60" height="4" fill="#404040" rx="1"/>`;
    svg += `<rect x="${scaleX + 28}" y="${barY}" width="${60 * strategy.current}" height="4" fill="${accent}" rx="1" opacity="0.6"/>`;
    svg += `<line x1="${scaleX + 28 + 60 * strategy.value}" y1="${barY - 1}"
            x2="${scaleX + 28 + 60 * strategy.value}" y2="${barY + 5}" stroke="#f1c40f" stroke-width="2"/>`;

    // Alignment indicator
    svg += `<circle cx="${scaleX + 92}" cy="${itemY + 10}" r="3" fill="${alignmentColor}"/>`;
    svg += `<text x="${scaleX + 28}" y="${itemY + 35}" font-family="system-ui,sans-serif" font-size="7" fill="${textS}">
            ${fmt(strategy.current * 100, 0)}% → ${fmt(strategy.value * 100, 0)}% (${fmt(alignment * 100, 0)}% aligned)</text>`;
  });

  // Evidence strength gauge (top-center)
  const gaugeX = W/2 - 30, gaugeY = 40, gaugeR = 30;
  const startAngle = -90, endAngle = 90;
  const valueAngle = startAngle + (endAngle - startAngle) * evidence.evidenceStrength;

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  // Gauge background
  svg += `<path d="${describeArc(gaugeX, gaugeY, gaugeR, startAngle, endAngle)}"
          fill="none" stroke="#404040" stroke-width="6" stroke-linecap="round"/>`;
  // Gauge value
  svg += `<path d="${describeArc(gaugeX, gaugeY, gaugeR, startAngle, valueAngle)}"
          fill="none" stroke="${bandColor}" stroke-width="6" stroke-linecap="round"/>`;

  const tickPos = polarToCartesian(gaugeX, gaugeY, gaugeR, valueAngle);
  svg += `<circle cx="${tickPos.x}" cy="${tickPos.y}" r="4" fill="${bandColor}"/>`;

  svg += `<text x="${gaugeX}" y="${gaugeY + 50}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="10" fill="${textP}">
          Evidence ${fmt(evidence.evidenceStrength * 100, 0)}%</text>`;
  svg += `<text x="${gaugeX}" y="${gaugeY + 65}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8" fill="${textS}">
          ${band} Complexity</text>`;

  // Strategy triangle (top-right)
  const triX = W - 80, triY = 50, triR = 35;
  const axes = [
    { key: "explore", label: "E", angle: -90, target: targets.explore, current: currentStrategy.explore },
    { key: "accommodate", label: "A", angle: 30, target: targets.accommodate, current: currentStrategy.accommodate },
    { key: "resilience", label: "R", angle: 150, target: targets.resilience, current: currentStrategy.resilience }
  ];

  // Triangle background
  svg += `<circle cx="${triX}" cy="${triY}" r="${triR}" fill="none" stroke="#404040" stroke-width="1" opacity="0.3"/>`;
  svg += `<circle cx="${triX}" cy="${triY}" r="${triR * 0.67}" fill="none" stroke="#404040" stroke-width="1" opacity="0.2"/>`;
  svg += `<circle cx="${triX}" cy="${triY}" r="${triR * 0.33}" fill="none" stroke="#404040" stroke-width="1" opacity="0.1"/>`;

  // Axes and labels
  axes.forEach(axis => {
    const end = polarToCartesian(triX, triY, triR, axis.angle);
    svg += `<line x1="${triX}" y1="${triY}" x2="${end.x}" y2="${end.y}" stroke="#404040" stroke-width="1"/>`;

    const labelPos = polarToCartesian(triX, triY, triR + 12, axis.angle);
    svg += `<text x="${labelPos.x}" y="${labelPos.y + 3}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="10" fill="${textS}">
            ${axis.label}</text>`;
  });

  // Current strategy (faint triangle)
  const currentPoints = axes.map(axis => polarToCartesian(triX, triY, triR * axis.current, axis.angle));
  const currentPath = currentPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  svg += `<path d="${currentPath}" fill="rgba(30, 130, 196, 0.2)" stroke="rgba(30, 130, 196, 0.5)" stroke-width="1"/>`;

  // Target strategy (bold triangle)
  const targetPoints = axes.map(axis => polarToCartesian(triX, triY, triR * axis.target, axis.angle));
  const targetPath = targetPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  svg += `<path d="${targetPath}" fill="rgba(241, 196, 15, 0.3)" stroke="${warn}" stroke-width="2"/>`;

  // Strategy value bars (bottom)
  const barY = H - 60;
  const barSpacing = (W - 80) / 3;
  axes.forEach((axis, i) => {
    const barX = 40 + i * barSpacing;
    const barW = 60, barH = 8;

    // Background bar
    svg += `<rect x="${barX}" y="${barY}" width="${barW}" height="${barH}" fill="#404040" rx="2"/>`;
    // Current value
    svg += `<rect x="${barX}" y="${barY}" width="${barW * axis.current}" height="${barH}" fill="${accent}" rx="2" opacity="0.6"/>`;
    // Target value
    svg += `<rect x="${barX}" y="${barY}" width="${barW * axis.target}" height="${barH}" fill="${warn}" rx="2" opacity="0.8"/>`;

    svg += `<text x="${barX + barW/2}" y="${barY + barH + 15}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="9" fill="${textP}">
            ${axis.key.charAt(0).toUpperCase() + axis.key.slice(1)}</text>`;
    svg += `<text x="${barX + barW/2}" y="${barY + barH + 28}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8" fill="${textS}">
            ${fmt(axis.current * 100, 0)}% → ${fmt(axis.target * 100, 0)}%</text>`;
  });

  // Evidence breakdown (right side)
  const minutiaeCount = Array.isArray(features) ?
    features.filter(f => f && (f.op === 'add' || (!f.op && f.x !== undefined && f.y !== undefined))).length : 0;
  const totalDwell = dwell.reduce((s, d) => s + d, 0) || 1;
  const activeCells = dwell.filter(d => d > 0).length;
  const avgClarity = clarityGrid ? clarityGrid.reduce((s, v) => s + v, 0) / clarityGrid.length : 0.5;

  const evidenceFactors = [
    { name: "Image Quality", value: evidence.qualityScore, raw: `${fmt(avgClarity * 100, 0)}% clarity` },
    { name: "Feature Count", value: evidence.quantityScore, raw: `${minutiaeCount} minutiae` },
    { name: "Coverage", value: evidence.coverageScore, raw: `${fmt((activeCells / (cols * rows)) * 100, 0)}% area` }
  ];

  const evidenceX = W - 120, evidenceY = 50;
  svg += `<rect x="${evidenceX - 5}" y="${evidenceY - 10}" width="110" height="120"
          fill="#000" opacity="0.3" rx="8"/>`;
  svg += `<text x="${evidenceX + 50}" y="${evidenceY - 2}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="${textP}">
          Evidence Breakdown</text>`;

  evidenceFactors.forEach((factor, i) => {
    const itemY = evidenceY + 15 + i * 30;
    const factorColor = factor.value >= 0.7 ? "#27ae60" : factor.value >= 0.4 ? "#f39c12" : "#e74c3c";

    svg += `<circle cx="${evidenceX + 15}" cy="${itemY + 5}" r="6" fill="${factorColor}" opacity="0.8"/>`;
    svg += `<text x="${evidenceX + 15}" y="${itemY + 9}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="8" font-weight="bold" fill="#fff">
            ${fmt(factor.value * 100, 0)}</text>`;

    svg += `<text x="${evidenceX + 25}" y="${itemY + 3}" font-family="system-ui,sans-serif" font-size="9" fill="${textP}">
            ${xmlEscape(factor.name)}</text>`;
    svg += `<text x="${evidenceX + 25}" y="${itemY + 13}" font-family="system-ui,sans-serif" font-size="7" fill="${textS}">
            ${xmlEscape(factor.raw)}</text>`;
  });

  // Overall complexity indicator
  svg += `<text x="${evidenceX + 50}" y="${evidenceY + 110}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="9" font-weight="bold" fill="${bandColor}">
          ${band} Complexity (${fmt(evidence.complexity * 100, 0)}%)</text>`;

  // Legend
  svg += `<g font-family="system-ui,sans-serif" font-size="11" fill="${textP}">
    <text x="10" y="18">${xmlEscape('Strategic Recommendations: Blue=explore, Purple=balance, Orange=exploit')}</text>
  </g>`;

  svg += `</svg>`;
  return svg;
}

/* -------------------------------------------------------
 * CONFIDENCE HALOS (evidence support: dwell × clarity)
 * -----------------------------------------------------*/
export function generateConfidenceHalosSVG({
  imageWidth: W, imageHeight: H,
  path, features, // features: [{op:'add'|'delete', x,y,t,quality?}]
  grid = { cols: 10, rows: 8 },
  clarityGrid = null,
  bgImageDataUrl = null,
  radiusPx = 28 // neighborhood radius in pixels for dwell integration
}) {
  const cols = grid.cols, rows = grid.rows;
  const cellW = W / cols, cellH = H / rows;

  const textP = cssVar('--text-primary', '#e0e0e0');
  const border = cssVar('--border', '#404040');
  const goodC  = cssVar('--success-color', '#2ecc71');
  const warnC  = cssVar('--warning-color', '#f1c40f');

  // dwell per cell (seconds)
  const dwellMs = new Array(rows*cols).fill(0);
  if (Array.isArray(path) && path.length>1){
    let prev = path[0];
    for (let i=1;i<path.length;i++){
      const p = path[i];
      const dt = Math.max(0,(p.t??0)-(prev.t??0));
      const cx = clamp(Math.floor(prev.x / cellW), 0, cols-1);
      const cy = clamp(Math.floor(prev.y / cellH), 0, rows-1);
      dwellMs[cy*cols + cx] += dt;
      prev = p;
    }
  }
  const dwellS = dwellMs.map(ms=> ms/1000);
  const clarity = clarityGrid || new Array(rows * cols).fill(1);

  // Prepare halos for add features only
  const adds = (features||[]).filter(f => f && f.op === 'add' && Number.isFinite(f.x) && Number.isFinite(f.y));

  const halos = adds.map(pt => {
    const cx = clamp(Math.floor(pt.x / cellW), 0, cols-1);
    const cy = clamp(Math.floor(pt.y / cellH), 0, rows-1);
    const rx = Math.max(1, Math.round(radiusPx / cellW));
    const ry = Math.max(1, Math.round(radiusPx / cellH));
    let effort = 0;
    for (let dy=-ry; dy<=ry; dy++){
      for (let dx=-rx; dx<=rx; dx++){
        const nx=cx+dx, ny=cy+dy;
        if (nx<0||nx>=cols||ny<0||ny>=rows) continue;
        const idx = ny*cols + nx;
        effort += dwellS[idx] * (clarity[idx] ?? 1);
      }
    }
    return { x: pt.x, y: pt.y, support: effort };
  });

  const maxSup = Math.max(1e-6, ...halos.map(h=>h.support));
  // map support → radius/opacity
  halos.forEach(h=>{
    h.r = 6 + 18 * Math.sqrt(h.support / maxSup); // 6..24 px
    h.op = 0.25 + 0.55 * (h.support / maxSup);    // 0.25..0.8
    h.color = h.support / maxSup >= 0.4 ? goodC : warnC;
  });

  // SVG
  let svg = '';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;
  svg += `<title>${xmlEscape('Confidence Halos')}</title>`;
  svg += `<desc>${xmlEscape('Support around added minutiae: radius/brightness scale with dwell × clarity')}</desc>`;
  if (bgImageDataUrl) {
    svg += `<image href="${xmlEscape(bgImageDataUrl)}" x="0" y="0" width="${W}" height="${H}" opacity="0.25" />`;
  } else {
    svg += `<rect x="0" y="0" width="${W}" height="${H}" fill="none" stroke="${border}" stroke-width="1"/>`;
  }

  halos.forEach(h=>{
    svg += `<circle cx="${fmt(h.x,1)}" cy="${fmt(h.y,1)}" r="${fmt(h.r,1)}" fill="${h.color}" opacity="${fmt(h.op,2)}" />`;
    svg += `<circle cx="${fmt(h.x,1)}" cy="${fmt(h.y,1)}" r="${fmt(Math.max(3,h.r-3),1)}" fill="none" stroke="#fff" stroke-width="1.5" opacity="0.8"/>`;
  });

  svg += `<g font-family="system-ui,sans-serif" font-size="12" fill="${textP}">
    <text x="10" y="18">${xmlEscape('Big/bright = well-supported; small/dim = review')}</text>
  </g>`;

  svg += `</svg>`;
  return svg;
}

/* -------------------------------------------------------
 * POSTURE & ALIGNMENT VISUALIZATION (replaces hesitation map)
 * Based on dynamic equilibrium alignment and posture classification
 * -----------------------------------------------------*/

// Posture determination based on strategy recommendations
function determinePosture(complexity, recommended) {
  const { explore, accommodate, resilience } = recommended;
  const band = complexity >= 0.66 ? "High" : complexity >= 0.33 ? "Medium" : "Low";

  if (band === "High" && explore >= 0.6 && accommodate >= 0.6 && resilience >= 0.6) {
    return {
      name: "Scout",
      description: "Broaden search; diversify features; stress-test interpretations",
      color: "#e74c3c"
    };
  } else if (band === "Medium" && explore >= 0.4 && explore <= 0.6 &&
             accommodate >= 0.4 && accommodate <= 0.6 &&
             resilience >= 0.4 && resilience <= 0.6) {
    return {
      name: "Weaver",
      description: "Integrate; compare alternatives; converge deliberately",
      color: "#f39c12"
    };
  } else if (band === "Low" && explore <= 0.4 && accommodate <= 0.4 && resilience <= 0.4) {
    return {
      name: "Miner",
      description: "Exploit consistent signals; refine; streamline",
      color: "#27ae60"
    };
  } else if (band === "Low" && resilience >= 0.6) {
    return {
      name: "Anchor",
      description: "Protect against regressions; confirm stability; document",
      color: "#3498db"
    };
  } else {
    // Tie-break by highest value
    const max = Math.max(explore, accommodate, resilience);
    if (explore === max) return { name: "Scout", description: "Explore-oriented approach", color: "#e74c3c" };
    if (accommodate === max) return { name: "Weaver", description: "Accommodate-oriented approach", color: "#f39c12" };
    return { name: "Anchor", description: "Resilience-oriented approach", color: "#3498db" };
  }
}

// Calculate alignment scores
function computeAlignmentScores(current, targets) {
  const align = (c, t) => 1 - Math.abs(c - t);

  const exploreAlign = align(current.explore || 0.5, targets.explore);
  const accommodateAlign = align(current.accommodate || 0.5, targets.accommodate);
  const resilienceAlign = align(current.resilience || 0.5, targets.resilience);
  const overall = (exploreAlign + accommodateAlign + resilienceAlign) / 3;

  return { exploreAlign, accommodateAlign, resilienceAlign, overall };
}

export function generateHesitationMapSVG({
  imageWidth: W, imageHeight: H,
  path, // trimmed [{x,y,t}]
  grid = { cols: 10, rows: 8 },
  clarityGrid = null,
  bgImageDataUrl = null,
  features = [], // minutiae features (should include {op:'add', x, y} objects)
  currentStrategy = { explore: 0.5, accommodate: 0.5, resilience: 0.5 }
}) {
  const cols = grid.cols, rows = grid.rows;
  const cellW = W / cols, cellH = H / rows;

  const textP = cssVar('--text-primary', '#e0e0e0');
  const textS = cssVar('--text-secondary', '#b0b0b0');
  const border = cssVar('--border', '#404040');
  const accent = cssVar('--accent-color', '#1982c4');

  // Calculate dwell per cell for evidence calculation
  const dwell = new Array(rows * cols).fill(0);
  if (Array.isArray(path) && path.length > 1) {
    let prev = path[0];
    for (let i = 1; i < path.length; i++) {
      const p = path[i];
      const dt = Math.max(0, (p.t ?? 0) - (prev.t ?? 0));
      const cx = clamp(Math.floor(prev.x / cellW), 0, cols - 1);
      const cy = clamp(Math.floor(prev.y / cellH), 0, rows - 1);
      dwell[cy * cols + cx] += dt;
      prev = p;
    }
  }

  // Compute evidence metrics from actual fingerprint data
  const evidence = computeEvidenceStrength(clarityGrid, dwell, cols, rows);
  const targets = computeStrategyTargets(evidence.complexity);
  const recommended = {
    explore: clamp(currentStrategy.explore + 1.0 * (targets.explore - currentStrategy.explore), 0, 1),
    accommodate: clamp(currentStrategy.accommodate + 1.0 * (targets.accommodate - currentStrategy.accommodate), 0, 1),
    resilience: clamp(currentStrategy.resilience + 1.0 * (targets.resilience - currentStrategy.resilience), 0, 1)
  };

  const alignment = computeAlignmentScores(currentStrategy, targets);
  const posture = determinePosture(evidence.complexity, recommended);

  // Calculate additional fingerprint-specific metrics
  const totalDwell = dwell.reduce((s, d) => s + d, 0) || 1;
  const activeCells = dwell.filter(d => d > 0).length;
  const coverageRate = activeCells / (cols * rows);
  const avgClarity = clarityGrid ? clarityGrid.reduce((s, v) => s + v, 0) / clarityGrid.length : 0.5;

  // Fix minutiae counting - look for features with 'add' operation or just count all features if no op field
  const minutiaeCount = Array.isArray(features) ?
    features.filter(f => f && (f.op === 'add' || (!f.op && f.x !== undefined && f.y !== undefined))).length : 0;

  const dwellTime = totalDwell / 1000; // seconds

  // Evidence factors that drive the posture
  const factors = [
    {
      name: "Image Clarity",
      value: avgClarity,
      impact: evidence.qualityScore,
      description: `${fmt(avgClarity * 100, 0)}% avg clarity → ${evidence.qualityScore > 0.7 ? 'enables focus' : evidence.qualityScore > 0.4 ? 'requires balance' : 'demands exploration'}`
    },
    {
      name: "Area Coverage",
      value: coverageRate,
      impact: evidence.coverageScore,
      description: `${fmt(coverageRate * 100, 0)}% coverage → ${evidence.coverageScore > 0.7 ? 'comprehensive' : evidence.coverageScore > 0.4 ? 'moderate' : 'incomplete'}`
    },
    {
      name: "Feature Density",
      value: minutiaeCount / 40,
      impact: evidence.quantityScore,
      description: `${minutiaeCount} minutiae → ${evidence.quantityScore > 0.7 ? 'rich evidence' : evidence.quantityScore > 0.4 ? 'adequate data' : 'sparse features'}`
    },
    {
      name: "Analysis Time",
      value: clamp(dwellTime / 120, 0, 1),
      impact: 1 - (dwellTime > 180 ? 0.3 : dwellTime > 120 ? 0.1 : 0),
      description: `${fmt(dwellTime, 0)}s analysis → ${dwellTime > 180 ? 'thorough' : dwellTime > 120 ? 'adequate' : 'rapid'}`
    }
  ];

  // SVG
  let svg = '';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;
  svg += `<title>${xmlEscape('Strategic Posture Analysis')}</title>`;
  svg += `<desc>${xmlEscape('Evidence-driven posture classification showing contributing factors')}</desc>`;

  if (bgImageDataUrl) {
    svg += `<image href="${xmlEscape(bgImageDataUrl)}" x="0" y="0" width="${W}" height="${H}" opacity="0.3" />`;
  } else {
    svg += `<rect x="0" y="0" width="${W}" height="${H}" fill="none" stroke="${border}" stroke-width="1"/>`;
  }

  // Add evidence overlay on fingerprint image
  // Dwell intensity overlay
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (dwell[idx] <= 0) continue;

      const x = c * cellW, y = r * cellH;
      const dwellIntensity = dwell[idx] / Math.max(...dwell);
      const clarity = clarityGrid ? clarityGrid[idx] || 0 : 0.5;

      // Color based on evidence quality: green=good, yellow=medium, red=poor
      const evidenceQuality = clarity * dwellIntensity;
      const color = evidenceQuality > 0.6 ? "#27ae60" : evidenceQuality > 0.3 ? "#f39c12" : "#e74c3c";

      svg += `<rect x="${fmt(x,1)}" y="${fmt(y,1)}" width="${fmt(cellW,1)}" height="${fmt(cellH,1)}"
              fill="${color}" opacity="${fmt(0.1 + 0.3 * dwellIntensity, 2)}"/>`;
    }
  }

  // Mark minutiae locations if available
  if (Array.isArray(features) && features.length > 0) {
    features.forEach(feature => {
      if (feature && feature.x !== undefined && feature.y !== undefined) {
        const isAdd = feature.op === 'add' || !feature.op;
        const color = isAdd ? "#fff" : "#ff4444";
        const size = isAdd ? 4 : 3;
        svg += `<circle cx="${feature.x}" cy="${feature.y}" r="${size}" fill="${color}" opacity="0.8" stroke="#000" stroke-width="1"/>`;
      }
    });
  }

  // Posture scale (left side)
  const allPostures = [
    { name: "Scout", color: "#e74c3c", complexity: "High", description: "Broaden search; diversify features" },
    { name: "Weaver", color: "#f39c12", complexity: "Med", description: "Integrate; compare alternatives" },
    { name: "Miner", color: "#27ae60", complexity: "Low", description: "Exploit consistent signals" },
    { name: "Anchor", color: "#3498db", complexity: "Low+", description: "Protect against regressions" }
  ];

  const scaleX = 15, scaleY = 60, scaleItemH = 35;
  svg += `<rect x="${scaleX - 5}" y="${scaleY - 10}" width="100" height="${allPostures.length * scaleItemH + 20}"
          fill="#000" opacity="0.3" rx="8"/>`;
  svg += `<text x="${scaleX + 45}" y="${scaleY - 2}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="${textP}">
          Posture Scale</text>`;

  allPostures.forEach((p, i) => {
    const itemY = scaleY + 10 + i * scaleItemH;
    const isActive = p.name === posture.name;

    // Posture indicator
    svg += `<circle cx="${scaleX + 15}" cy="${itemY + 8}" r="8" fill="${p.color}" opacity="${isActive ? 1 : 0.4}"
            stroke="${isActive ? '#fff' : 'none'}" stroke-width="${isActive ? 2 : 0}"/>`;

    // Labels
    svg += `<text x="${scaleX + 28}" y="${itemY + 6}" font-family="system-ui,sans-serif" font-size="9"
            font-weight="${isActive ? 'bold' : 'normal'}" fill="${isActive ? textP : textS}">
            ${xmlEscape(p.name)}</text>`;
    svg += `<text x="${scaleX + 28}" y="${itemY + 16}" font-family="system-ui,sans-serif" font-size="7" fill="${textS}">
            ${xmlEscape(p.complexity)} complexity</text>`;
    svg += `<text x="${scaleX + 28}" y="${itemY + 25}" font-family="system-ui,sans-serif" font-size="7" fill="${textS}" opacity="0.8">
            ${xmlEscape(p.description)}</text>`;
  });

  // Central posture circle (enneagram-style)
  const centerX = W/2, centerY = H/2, centerR = 60;
  const outerR = centerR + 80;

  // Complexity ring
  const complexityAngle = evidence.complexity * 360;
  svg += `<circle cx="${centerX}" cy="${centerY}" r="${centerR + 20}" fill="none" stroke="#404040" stroke-width="2" opacity="0.3"/>`;

  // Complexity indicator
  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
  };

  const complexityColor = evidence.complexity >= 0.66 ? "#c0392b" : evidence.complexity >= 0.33 ? "#f39c12" : "#27ae60";
  const complexityPos = polarToCartesian(centerX, centerY, centerR + 20, complexityAngle);
  svg += `<circle cx="${complexityPos.x}" cy="${complexityPos.y}" r="6" fill="${complexityColor}"/>`;
  svg += `<text x="${complexityPos.x}" y="${complexityPos.y - 12}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8" fill="${textS}">
          ${evidence.complexity >= 0.66 ? 'High' : evidence.complexity >= 0.33 ? 'Med' : 'Low'} Complexity</text>`;

  // Central posture badge
  svg += `<circle cx="${centerX}" cy="${centerY}" r="${centerR}" fill="${posture.color}" opacity="0.9" stroke="#fff" stroke-width="3"/>`;
  svg += `<text x="${centerX}" y="${centerY - 5}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="16" font-weight="bold" fill="#fff">
          ${xmlEscape(posture.name)}</text>`;
  svg += `<text x="${centerX}" y="${centerY + 15}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="9" fill="#fff" opacity="0.9">
          Strategic Posture</text>`;

  // Evidence factor spokes (like enneagram points)
  factors.forEach((factor, i) => {
    const angle = (i * 90) - 45; // Spread factors around circle
    const spokeEnd = polarToCartesian(centerX, centerY, outerR, angle);
    const labelPos = polarToCartesian(centerX, centerY, outerR + 25, angle);

    // Spoke line with intensity based on impact
    const spokeColor = factor.impact >= 0.7 ? "#27ae60" : factor.impact >= 0.4 ? "#f39c12" : "#e74c3c";
    const spokeWidth = 2 + factor.impact * 4;
    svg += `<line x1="${centerX}" y1="${centerY}" x2="${spokeEnd.x}" y2="${spokeEnd.y}"
            stroke="${spokeColor}" stroke-width="${spokeWidth}" opacity="0.8"/>`;

    // Impact indicator circle
    svg += `<circle cx="${spokeEnd.x}" cy="${spokeEnd.y}" r="${8 + factor.impact * 6}"
            fill="${spokeColor}" opacity="0.7"/>`;
    svg += `<text x="${spokeEnd.x}" y="${spokeEnd.y + 3}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="#fff">
            ${fmt(factor.impact * 100, 0)}</text>`;

    // Factor label
    svg += `<text x="${labelPos.x}" y="${labelPos.y - 5}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="${textP}">
            ${xmlEscape(factor.name)}</text>`;
    svg += `<text x="${labelPos.x}" y="${labelPos.y + 8}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="8" fill="${textS}">
            ${xmlEscape(factor.description)}</text>`;
  });

  // Strategy vectors (top corners)
  const strategyLabels = [
    { key: 'explore', label: 'Explore', current: currentStrategy.explore, target: targets.explore, x: 50, y: 30 },
    { key: 'accommodate', label: 'Accommodate', current: currentStrategy.accommodate, target: targets.accommodate, x: W/2, y: 20 },
    { key: 'resilience', label: 'Resilience', current: currentStrategy.resilience, target: targets.resilience, x: W-50, y: 30 }
  ];

  strategyLabels.forEach(strat => {
    const gap = Math.abs(strat.current - strat.target);
    const gapColor = gap < 0.2 ? "#27ae60" : gap < 0.4 ? "#f39c12" : "#e74c3c";

    svg += `<rect x="${strat.x - 25}" y="${strat.y}" width="50" height="12" fill="#404040" rx="2"/>`;
    svg += `<rect x="${strat.x - 25}" y="${strat.y}" width="${50 * strat.current}" height="12" fill="${accent}" rx="2" opacity="0.6"/>`;
    svg += `<line x1="${strat.x - 25 + 50 * strat.target}" y1="${strat.y - 2}"
            x2="${strat.x - 25 + 50 * strat.target}" y2="${strat.y + 14}" stroke="#f1c40f" stroke-width="2"/>`;

    svg += `<text x="${strat.x}" y="${strat.y - 5}" text-anchor="middle"
            font-family="system-ui,sans-serif" font-size="9" fill="${textP}">
            ${strat.label}</text>`;
    svg += `<circle cx="${strat.x + 35}" cy="${strat.y + 6}" r="3" fill="${gapColor}"/>`;
  });

  // Recommended action (bottom)
  const actionY = H - 30;
  svg += `<rect x="20" y="${actionY - 15}" width="${W - 40}" height="25" fill="${posture.color}" rx="6" opacity="0.2"/>`;
  svg += `<text x="${W/2}" y="${actionY - 5}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="10" font-weight="bold" fill="${textP}">
          Recommended Action:</text>`;
  svg += `<text x="${W/2}" y="${actionY + 8}" text-anchor="middle"
          font-family="system-ui,sans-serif" font-size="9" fill="${textS}">
          ${xmlEscape(posture.description)}</text>`;

  // Legend
  svg += `<g font-family="system-ui,sans-serif" font-size="10" fill="${textP}">
    <text x="10" y="18">${xmlEscape('Posture driven by fingerprint evidence complexity and strategic alignment')}</text>
  </g>`;

  svg += `</svg>`;
  return svg;
}

/* -------------------------------------------------------
 * POSTER: 4 QUADRANTS (LANDSCAPE 10×8 @ 300dpi base)
 * -----------------------------------------------------*/
export function assembleStrategyPosterLandscapeSVG({
  posterWidth = 3000,  // 10" @ 300dpi
  posterHeight = 2400, // 8" @ 300dpi
  margin = 80,
  gutter = 40,
  // four prebuilt SVG strings:
  coverageSvg,
  routeSvg,
  halosSvg,
  hesitationSvg,
  header = 'Strategy Poster',
  subHeader = ''
}) {
  const textP  = cssVar('--text-primary', '#e0e0e0');
  const textS  = cssVar('--text-secondary', '#b0b0b0');
  const border = cssVar('--border', '#404040');
  const bg     = cssVar('--background-secondary', '#22252a');

  const W = posterWidth, H = posterHeight;
  const innerW = W - margin * 2;
  const innerH = H - margin * 2 - 100; // header area
  const colW = (innerW - gutter) / 2;
  const rowH = (innerH - gutter) / 2;

  const covURI = svgToDataUri(coverageSvg);
  const routeURI = svgToDataUri(routeSvg);
  const halosURI = svgToDataUri(halosSvg);
  const hesURI = svgToDataUri(hesitationSvg);

  const label = (x, y, text) =>
    `<text x="${fmt(x,1)}" y="${fmt(y,1)}" font-family="system-ui,sans-serif" font-size="28" fill="${textP}">${xmlEscape(text)}</text>`;

  const panel = (x,y,w,h) => `<rect x="${fmt(x-1,1)}" y="${fmt(y-1,1)}" width="${fmt(w+2,1)}" height="${fmt(h+2,1)}" fill="none" stroke="${border}" stroke-width="2"/>`;

  const topY = margin + 100;
  const leftX = margin;
  const rightX = margin + colW + gutter;
  const midY = topY + rowH + gutter;

  let svg = '';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;
  svg += `<title>${xmlEscape('Strategy Poster (Landscape)')}</title>`;
  svg += `<desc>${xmlEscape('Coverage, Dynamic Strategy, Confidence Halos, Strategy Posture')}</desc>`;
  svg += `<rect x="0" y="0" width="${W}" height="${H}" fill="${bg}"/>`;

  // Header
  svg += `<g font-family="system-ui,sans-serif">
    <text x="${margin}" y="${margin+36}" font-size="36" font-weight="700" fill="${textP}">${xmlEscape(header)}</text>
    <text x="${margin}" y="${margin+70}" font-size="22" fill="${textS}">${xmlEscape(subHeader)}</text>
  </g>`;

  // TL: Coverage
  svg += `<image href="${covURI}" x="${fmt(leftX,1)}" y="${fmt(topY,1)}" width="${fmt(colW,1)}" height="${fmt(rowH,1)}" preserveAspectRatio="xMidYMid meet"/>`;
  svg += panel(leftX, topY, colW, rowH);
  svg += label(leftX, topY - 10, 'Coverage Grid');

  // TR: Dynamic Strategy
  svg += `<image href="${routeURI}" x="${fmt(rightX,1)}" y="${fmt(topY,1)}" width="${fmt(colW,1)}" height="${fmt(rowH,1)}" preserveAspectRatio="xMidYMid meet"/>`;
  svg += panel(rightX, topY, colW, rowH);
  svg += label(rightX, topY - 10, 'Dynamic Strategy');

  // BL: Confidence Halos
  svg += `<image href="${halosURI}" x="${fmt(leftX,1)}" y="${fmt(midY,1)}" width="${fmt(colW,1)}" height="${fmt(rowH,1)}" preserveAspectRatio="xMidYMid meet"/>`;
  svg += panel(leftX, midY, colW, rowH);
  svg += label(leftX, midY - 10, 'Confidence Halos');

  // BR: Strategy Posture
  svg += `<image href="${hesURI}" x="${fmt(rightX,1)}" y="${fmt(midY,1)}" width="${fmt(colW,1)}" height="${fmt(rowH,1)}" preserveAspectRatio="xMidYMid meet"/>`;
  svg += panel(rightX, midY, colW, rowH);
  svg += label(rightX, midY - 10, 'Strategy Posture');

  svg += `</svg>`;
  return svg;
}
